<script>
  import Clickoutside from '../../utils/clickoutside';
  import Emitter from '../../mixins/emitter';
  import ZmButton from '../button';
  import ZmButtonGroup from '../button-group';
  import { generateId } from '../../utils/util';
  import AriaUtils from '../../utils/aria-utils';
  import NavigationMixin from './navigation-mixin';

  export default {
    name: 'ZmDropdown',

    componentName: 'ZmDropdown',

    mixins: [Emitter, NavigationMixin],

    directives: { Clickoutside },

    components: {
      ZmButton,
      ZmButtonGroup
    },

    provide() {
      return {
        dropdown: this
      };
    },

    props: {
      trigger: {
        type: String,
        default: 'click'
      },
      type: String,
      size: {
        type: String,
        default: ''
      },
      splitButton: Boolean,
      hideOnClick: {
        type: Boolean,
        default: true
      },
      placement: {
        type: String,
        default: 'bottom-end'
      },
      visibleArrow: {
        type: Boolean,
        default: true
      },
      showTimeout: {
        type: Number,
        default: 250
      },
      hideTimeout: {
        type: Number,
        default: 150
      },
      disabled: Boolean,
      disableFocus: Boolean,
      shouldHoverIndex: Boolean
    },

    data() {
      return {
        options: [],
        hoverIndex: -1,
        timeout: null,
        visible: false,
        triggerElm: null,
        menuItemsArray: null,
        dropdownElm: null,
        focusing: false,
        listId: `dropdown-menu-${generateId()}`
      };
    },

    computed: {
      dropdownSize() {
        return this.size || (this.$ELEMENT || {}).size;
      }
    },

    mounted() {
      this.$on('menu-item-click', this.handleMenuItemClick);

      this.$on('menu-list-keydown', this.handleListKeyDown);
    },

    watch: {
      visible(val) {
        // wait for the list show, for AX
        setTimeout(() => {
          this.broadcast('ZmDropdownMenu', 'focusList', val);
        }, 100);
        this.broadcast('ZmDropdownMenu', 'visible', val);
        this.$emit('visible-change', val);
      },
      focusing(val) {
        const selfDefine = this.$el.querySelector('.zm-dropdown-selfdefine');
        if (selfDefine) { // 自定义
          if (val) {
            selfDefine.className += ' focusing';
          } else {
            selfDefine.className = selfDefine.className.replace('focusing', '');
          }
        }
      },
      activedescendant(val) {
        this.broadcast('ZmDropdownMenu', 'activedescendant', val);
      },
      options() {
        this.$nextTick(() => {
          const menuItems = this.dropdownElm.querySelectorAll('.zm-dropdown-menu__item:not(.is-disabled)');
          this.menuItemsArray = Array.prototype.slice.call(menuItems);
        });
      }
    },

    methods: {
      show() {
        if (this.disabled) return;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.visible = true;
          this.triggerElm.setAttribute('aria-expanded', this.visible);
        }, this.trigger === 'click' ? 0 : this.showTimeout);
      },
      hide() {
        if (this.disabled) return;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.visible = false;
          this.triggerElm.setAttribute('aria-expanded', this.visible);
          if (!this.shouldHoverIndex) {
            this.hoverIndex = -1;
            this.activedescendant = null;
          }
        }, this.trigger === 'click' ? 0 : this.hideTimeout);
      },
      handleClick() {
        if (this.disabled) return;
        if (this.visible) {
          this.hide();
        } else {
          this.show();
        }
      },
      handleTriggerKeyDown(ev) {
        if (this.disabled) {
          return;
        }
        const keyCode = ev.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
          case keys.space:
          case keys.down:
            this.visible = true;
            ev.preventDefault();
            ev.stopPropagation();
            break;
        }
      },
      handleListKeyDown(ev) {
        if (this.disabled) {
          return;
        }
        const keyCode = ev.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
            if (this.hoverOption) {
              this.handleMenuItemClick(this.hoverOption.command, this.hoverOption);
            }
            ev.preventDefault();
            break;
          case keys.esc:
            this.handleEsc();
            ev.preventDefault();
            ev.stopPropagation();
            break;
          case keys.tab:
            this.hide();
            this.focus();
            break;
          case keys.down:
            this.navigateOptions('next');
            ev.stopPropagation();
            ev.preventDefault();
            break;
          case keys.up:
            this.navigateOptions('prev');
            ev.stopPropagation();
            ev.preventDefault();
            break;
          case keys.home:
            this.navigateOptions('home');
            ev.stopPropagation();
            ev.preventDefault();
            break;
          case keys.end:
            this.navigateOptions('end');
            ev.stopPropagation();
            ev.preventDefault();
            break;
        }
      },
      handleEsc() {
        this.visible = false;
        this.$nextTick(() => {
          this.focus();
        });
      },
      initAria() {
        this.dropdownElm.setAttribute('id', this.listId);
        this.triggerElm.setAttribute('role', 'button');
        this.triggerElm.setAttribute('aria-haspopup', 'listbox');
        this.triggerElm.setAttribute('aria-controls', this.listId);
        this.triggerElm.setAttribute('aria-expanded', this.visible);
        const menuItems = this.dropdownElm.querySelectorAll('.zm-dropdown-menu__item:not(.is-disabled)');
        this.menuItemsArray = Array.prototype.slice.call(menuItems);

        if (!this.splitButton) { // 自定义
          // this.triggerElm.setAttribute('role', 'menu');
          this.triggerElm.setAttribute('tabindex', this.disableFocus ? '-1' : '0');
          this.triggerElm.setAttribute('class', (this.triggerElm.getAttribute('class') || '') + ' zm-dropdown-selfdefine'); // 控制
        }
      },
      initEvent() {
        let { trigger, show, hide, handleClick, splitButton, handleTriggerKeyDown, onTriggerElmFocus, onTriggerElmBlur, onTriggerElmClick } = this;
        this.triggerElm = splitButton
          ? this.$refs.trigger.$el
          : this.$slots.default[0].elm;

        const dropdownElm = this.dropdownElm;

        this.triggerElm.addEventListener('keydown', handleTriggerKeyDown); // triggerElm keydown
        // 控制自定义元素的样式
        if (!splitButton) {
          this.triggerElm.addEventListener('focus', onTriggerElmFocus);
          this.triggerElm.addEventListener('blur', onTriggerElmBlur);
          this.triggerElm.addEventListener('click', onTriggerElmClick);
        }
        if (trigger === 'hover') {
          this.triggerElm.addEventListener('mouseenter', show);
          this.triggerElm.addEventListener('mouseleave', hide);
          this.triggerElm.addEventListener('click', handleClick);
          dropdownElm.addEventListener('mouseenter', show);
          dropdownElm.addEventListener('mouseleave', hide);
        } else if (trigger === 'click') {
          this.triggerElm.addEventListener('click', handleClick);
        }
      },
      removeEvent() {
        let { trigger, show, hide, handleClick, splitButton, handleTriggerKeyDown, onTriggerElmFocus, onTriggerElmBlur, onTriggerElmClick } = this;
        this.triggerElm = splitButton
          ? this.$refs.trigger.$el
          : this.$slots.default[0].elm;

        const dropdownElm = this.dropdownElm;

        this.triggerElm.removeEventListener('keydown', handleTriggerKeyDown); // triggerElm keydown
        // 控制自定义元素的样式
        if (!splitButton) {
          this.triggerElm.removeEventListener('focus', onTriggerElmFocus);
          this.triggerElm.removeEventListener('blur', onTriggerElmBlur);
          this.triggerElm.removeEventListener('click', onTriggerElmClick);
        }
        if (trigger === 'hover') {
          this.triggerElm.removeEventListener('mouseenter', show);
          this.triggerElm.removeEventListener('mouseleave', hide);
          this.triggerElm.removeEventListener('click', handleClick);
          dropdownElm.removeEventListener('mouseenter', show);
          dropdownElm.removeEventListener('mouseleave', hide);
        } else if (trigger === 'click') {
          this.triggerElm.removeEventListener('click', handleClick);
        }
      },
      handleMenuItemClick(command, instance) {
        if (this.hideOnClick) {
          this.visible = false;
          this.triggerElm.setAttribute('aria-expanded', false);
          this.$nextTick(() => {
            this.focus();
          });
        }
        if (this.shouldHoverIndex) {
          this.hoverIndex = this.options.indexOf(instance);
        }
        this.$emit('command', command, instance);
      },
      focus() {
        this.triggerElm.focus && this.triggerElm.focus();
      },
      initDomOperation() {
        this.dropdownElm = this.popperElm;
        const menuItems = this.dropdownElm.querySelectorAll('.zm-dropdown-menu__item:not(.is-disabled)');
        this.menuItemsArray = [].slice.call(menuItems);
        this.removeEvent();
        this.initEvent();
        this.initAria();
      },
      onTriggerElmFocus() {
        this.focusing = true;
      },
      onTriggerElmBlur() {
        this.focusing = false;
      },
      onTriggerElmClick(e) {
        e.stopPropagation();
        this.focusing = false;
      }
    },

    beforeDestroy() {
      this.removeEvent();
    },

    render(h) {
      let { hide, splitButton, type, dropdownSize } = this;

      const handleMainButtonClick = (event) => {
        event.stopPropagation();
        this.$emit('click', event);
        hide();
      };

      let triggerElm = !splitButton
        ? this.$slots.default
        : (<zm-button-group>
          <zm-button type={type} size={dropdownSize} nativeOn-click={handleMainButtonClick}>
            {this.$slots.default}
          </zm-button>
          <zm-button ref="trigger" type={type} size={dropdownSize} class="zm-dropdown__caret-button"
            aria-label="Toggle dropdown">
            <i class="zm-dropdown__icon zm-icon-down" aria-hidden="true"></i>
          </zm-button>
        </zm-button-group>);

      return (
        <div class="zm-dropdown" v-clickoutside={hide}>
          {triggerElm}
          {this.$slots.dropdown}
        </div>
      );
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";
  @import "../../scss/common/popper";

  @include b(dropdown) {
    display: inline-block;
    position: relative;
    //color: $--color-text-primary;
    font-size: inherit;

    /*.zm-button {*/
    /*&:focus {*/
    /*box-shadow: none;*/
    /*}*/
    /*}*/
    .zm-button-group {
      // display: block;
      .zm-button {
        float: none;
      }
    }

    & .zm-dropdown__caret-button {
      padding-left: 5px;
      padding-right: 5px;
      position: relative;
      border-left: none;
      &.zm-button--small {
        // padding-top: $--button-small-padding-vertical;
      }
      &::before {
        $gap: 5px;

        content: '';
        position: absolute;
        display: block;
        width: 1px;
        top: $gap;
        bottom: $gap;
        left: 0;
        background: mix(white, transparent, 50%);
      }

      &:hover {
        &::before {
          top: 0;
          bottom: 0;
        }
      }

      & .zm-dropdown__icon {
        padding-left: 0;
      }
      & .zm-button__content {
        padding-left: 0;
        padding-right: 0;
      }
    }
    @include e(icon) {
      font-size: 12px;
      margin: 0 3px;
    }

    .zm-dropdown-selfdefine { // 自定义
      &:focus:active, &:focus:not(.focusing) {
        outline-width: 0;
      }
    }
  }
</style>
